import { CONFIG } from "@/components/FormEngine/config";
import {
  sortByIdIndex,
  sortFields,
  sortMapByIndex,
} from "@/components/FormEngine/helpers/sortFields";
import {
  FormField,
  FormRow,
  FormSection,
  FormStep,
  FormSubSection,
  RawField,
} from "@/components/FormEngine/types";
import { toNumber } from "@/components/FormEngine/utils";

type GroupInputItem = FormField | { field: FormField; rawData: RawField };
type GroupInput = Array<GroupInputItem>;

/**
 * Extracts grouping indexes from an item
 */
const extractIndexes = (item: GroupInputItem) => {
  const field: FormField = "field" in item ? item.field : item;
  const indexSource: Partial<
    RawField & Pick<FormField, "step" | "section" | "subSection" | "row">
  > = "rawData" in item ? item.rawData : field;

  return {
    field,
    stepIndex: toNumber(indexSource.step, CONFIG.DEFAULTS.STEP_INDEX),
    sectionIndex: toNumber(indexSource.section, CONFIG.DEFAULTS.SECTION_INDEX),
    subSectionIndex: toNumber(
      indexSource.subSection,
      CONFIG.DEFAULTS.SUB_SECTION_INDEX,
    ),
    rowIndex: toNumber(indexSource.row, CONFIG.DEFAULTS.ROW_INDEX),
  };
};

/**
 * Finds or creates a step
 */
const getOrCreateStep = (
  stepsByIndex: Map<number, FormStep>,
  stepIndex: number,
): FormStep => {
  if (!stepsByIndex.has(stepIndex)) {
    stepsByIndex.set(stepIndex, {
      id: `step-${stepIndex}`,
      sections: new Map<number, FormSection>(),
    });
  }

  return stepsByIndex.get(stepIndex)!;
};

/**
 * Finds or creates a section
 */
const getOrCreateSection = (
  step: FormStep,
  sectionIndex: number,
): FormSection => {
  if (!step.sections.has(sectionIndex)) {
    step.sections.set(sectionIndex, {
      id: `section-${sectionIndex}`,
      subSections: new Map<number, FormSubSection>(),
    });
  }

  return step.sections.get(sectionIndex)!;
};

/**
 * Finds or creates a subsection
 */
const getOrCreateSubSection = (
  section: FormSection,
  subSectionIndex: number,
): FormSubSection => {
  if (!section.subSections.has(subSectionIndex)) {
    section.subSections.set(subSectionIndex, {
      id: `subsection-${subSectionIndex}`,
      rows: [],
    });
  }

  return section.subSections.get(subSectionIndex)!;
};

/**
 * Finds or creates a row
 */
const getOrCreateRow = (
  subSection: FormSubSection,
  rowIndex: number,
): FormRow => {
  let row = subSection.rows.find((r) => r.id === `row-${rowIndex}`);
  if (!row) {
    row = {
      id: `row-${rowIndex}`,
      fields: [],
    };
    subSection.rows.push(row);
  }

  return row;
};

/**
 * Sorts all levels of the hierarchy
 */
const sortAllLevels = (stepsByIndex: Map<number, FormStep>): void => {
  // Sort at step level (already sorted by Map with numeric index)
  for (const [, step] of sortMapByIndex(stepsByIndex)) {
    // Sort at section level
    for (const [, section] of sortMapByIndex(step.sections)) {
      // Sort at subsection level
      for (const [, subSection] of sortMapByIndex(section.subSections)) {
        // Sort rows by index
        sortByIdIndex(subSection.rows, "row-");

        // Sort fields in each row
        for (const row of subSection.rows) {
          sortFields(row.fields);
        }
      }
    }
  }
};

/**
 * Builds the complete form hierarchy: Step > Section > SubSection > Row > Fields
 */
export const buildFormHierarchy = (
  items: GroupInput,
): Map<number, FormStep> => {
  const stepsByIndex = new Map<number, FormStep>();

  // Phase 1: Grouping fields in the hierarchy
  for (const item of items) {
    const { field, stepIndex, sectionIndex, subSectionIndex, rowIndex } =
      extractIndexes(item);

    const step = getOrCreateStep(stepsByIndex, stepIndex);
    const section = getOrCreateSection(step, sectionIndex);
    const subSection = getOrCreateSubSection(section, subSectionIndex);
    const row = getOrCreateRow(subSection, rowIndex);

    row.fields.push(field);
  }

  // Phase 2: Sort all levels
  sortAllLevels(stepsByIndex);

  return stepsByIndex;
};
